import { QueryError } from '@graphql-box/helpers';
import { type Movie, ScreenType, type Tv } from '@tmdb-graphql-api/schema/types';
import { type ReactElement, useEffect } from 'react';
import { formatWatchlist } from '#components/Watchlist/helpers/formatWatchlist.ts';
import { WatchlistTabs } from '#components/WatchlistTabs/index.tsx';
import { useMovieTvMultiQuery } from '#hooks/useMovieTvMultiQuery.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { useStore } from '#store/useStore.ts';
import { type ActivePreview } from '#types.ts';

export type WatchlistProps = {
  children?: (element?: ReactElement) => ReactElement | undefined;
  setActivePreview?: (item: ActivePreview) => void;
};

export const Watchlist = ({ children, setActivePreview }: WatchlistProps) => {
  const { config } = useToolbelt();
  const watchlist = useStore(store => store.watchlist);
  const watchlistMovies = watchlist.filter(entry => entry.type === ScreenType.Movie).map(entry => entry.id);
  const watchlistTv = watchlist.filter(entry => entry.type === ScreenType.Tv).map(entry => entry.id);

  const { execute, movieLoading, movieResults, tvLoading, tvResults } = useMovieTvMultiQuery({
    initiator: 'Watchlist',
  });

  const depsKey = JSON.stringify({ movies: watchlistMovies, tv: watchlistTv });

  useEffect(() => {
    execute(watchlistMovies, watchlistTv);
    // We only want to re-execute when id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey]);

  const movieData: Movie[] = [];
  const tvData: Tv[] = [];
  const errors: QueryError[] = [];

  for (const result of movieResults) {
    if (result instanceof QueryError) {
      errors.push(result);
    } else {
      movieData.push(result.data.movie);
    }
  }

  for (const result of tvResults) {
    if (result instanceof QueryError) {
      errors.push(result);
    } else {
      tvData.push(result.data.tv);
    }
  }

  if (errors.length > 0) {
    console.log(errors);
  }

  const { watchlistItems } = formatWatchlist(movieData, tvData, config);

  const watchlistElement = (
    <WatchlistTabs items={watchlistItems} loading={movieLoading || tvLoading} setActivePreview={setActivePreview} />
  );

  return children ? children(watchlistElement) : children;
};
